import React from 'react';
import { StyleSheet, View, TouchableOpacity, Image } from 'react-native';
import { Paragraph, ParagraphBold } from '../../../components';
import { SurveyQuestion } from '../../../api/survey/survey.models';
import { Config } from '../../../config';

interface SliderQuestionProps {
  question: SurveyQuestion;
  value?: number;
  onChange: (value: number) => void;
}

export const SliderQuestion: React.FC<SliderQuestionProps> = ({
  question,
  value,
  onChange,
}) => {
  const { localizedContent } = question;

  // Content is already localized from the server
  const content = localizedContent;

  if (!content || !content.options) return null;

  // Sort options in descending order (best first, worst last)
  const options = [...content.options].sort((a, b) => b.value - a.value);

  return (
    <View style={styles.container}>
      <ParagraphBold style={styles.questionText}>{content.text}</ParagraphBold>

      <View style={styles.optionsContainer}>
        {options.map((option) => {
          const isSelected = value === option.value;

          return (
            <TouchableOpacity
              key={option.value}
              style={[styles.optionCard, isSelected && styles.selectedCard]}
              onPress={() => onChange(option.value)}
              activeOpacity={0.7}
            >
              <Image
                source={{
                  uri: isSelected ? option.iconFull : option.iconEmpty,
                }}
                style={styles.optionIcon}
                resizeMode="contain"
              />
              <Paragraph
                style={
                  isSelected
                    ? StyleSheet.flatten([
                        styles.optionText,
                        styles.selectedText,
                      ])
                    : styles.optionText
                }
              >
                {option.text}
              </Paragraph>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 38,
    paddingHorizontal: 4,
  },
  questionText: {
    marginBottom: 20,
    fontSize: 16,
    lineHeight: 24,
  },
  optionsContainer: {
    gap: 8,
  },
  optionCard: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 8,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E5E7',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  selectedCard: {
    borderColor: Config.colorPrimary,
    backgroundColor: '#F8F9FF',
    shadowColor: Config.colorPrimary,
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  optionIcon: {
    width: 24,
    height: 24,
    marginRight: 16,
  },
  optionText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 22,
    color: '#1C1C1E',
  },
  selectedText: {
    fontWeight: '600',
    color: Config.colorPrimary,
  },
});
